/* eslint-disable react/prop-types,no-unused-vars */
import React, { useRef, useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import clsx from "clsx";

import parlorSocialClub from "assets/graphics/icons/parlor-social-club-black.svg";
import logoWithLines from "assets/graphics/icons/logo-with-lines.svg";
import pin2 from "assets/graphics/icons/pin-map-2.svg";
import ticket from "assets/graphics/icons/ticket-icon.png";
import { format, subMinutes, isWithinInterval } from "date-fns";

import "screens/events/component/view.scss";
import GoogleMap from "./map";
import pin from "assets/graphics/icons/pin-map.svg";
import ReactMarkdown from "react-markdown/with-html";

const EventsView = ({
  hash,
  isNominated,
  isLiveEvent,
  guestName,
  invitedBy,
  eventName,
  locationName,
  eventDate,
  eventEnd,
  eventDescription,
  eventTicketPrice,
  eventTicketCurrency,
  eventLocation,
  guests,
  addEvent,
  eventImages,
  showLiveEventTime,
}) => {
  const attendButtonClsx = clsx("outlined-button", "outlined-button--attend", {
    "outlined-button--inverted": isNominated,
  });

  const [ratio, setRatio] = useState("1x1");

  const heroRef = useRef(null);

  const calculateScreenRatio = () => {
    function findClosest(arrSorted, value) {
      let closest = arrSorted[0];
      let closestDiff = Math.abs(arrSorted[0] - value);
      for (let i = 1; i < arrSorted.length; i++) {
        let diff = Math.abs(arrSorted[i] - value);
        if (diff < closestDiff) {
          closestDiff = diff;
          closest = arrSorted[i];
        } else {
          return closest;
        }
      }
      return arrSorted[arrSorted.length - 1];
    }

    const AVAILABLE_RATIOS = [
      [1, "1x1"],
      [2, "2x1"],
      [3 / 4, "3x4"],
      [4 / 3, "4x3"],
      [4 / 5, "4x5"],
    ];

    const RATIOS = AVAILABLE_RATIOS.map((item) => item[0]).sort();
    const LOOKUP = {};

    AVAILABLE_RATIOS.forEach((item) => {
      LOOKUP[item[0]] = item[1];
    });

    if (!heroRef.current) {
      return "1x1";
    }

    const { width, height } =
      heroRef.current && heroRef.current.getBoundingClientRect();

    const ratio = Math.max(width, height) / Math.min(width, height);

    if (ratio in LOOKUP) {
      return LOOKUP[ratio];
    }

    const closest = findClosest(RATIOS, ratio);
    if (Math.abs(closest - ratio) <= 0.75) {
      return LOOKUP[closest] ? LOOKUP[closest] : "2x1";
    }

    return "2x1";
  };

  useEffect(() => {
    window.scrollTo({
      top: 0,
    });
  }, []);

  const parseDate = (dateString) => {
    const splittedDate = dateString
      .split(" ")[0]
      .split("-")
      .map((item) => Number(item))
      .map((item, index) => {
        if (index === 1) {
          return item - 1;
        }

        return item;
      });

    const splittedHours = dateString
      .split(" ")[1]
      .split(":")
      .map((item) => Number(item));

    return new Date(...[...splittedDate, ...splittedHours]);
  };

  const shouldDisplayLiveBtn = useMemo(() => {
    const start = subMinutes(parseDate(eventDate), showLiveEventTime);
    const end = parseDate(eventEnd);

    return isWithinInterval(new Date(), { start, end });
  }, [eventDate, eventEnd, showLiveEventTime]);

  useEffect(() => {
    setRatio(calculateScreenRatio());
  }, [heroRef.current]);

  return (
    <main className="events__page">
      <header className="events__page__header">
        <div className="img__wrapper">
          <Link to="/">
            <img src={parlorSocialClub} alt="logo" />
          </Link>
        </div>
      </header>
      <section className="hero" ref={heroRef}>
        <div className="hero__image-container">
          <picture className="hero__image">
            <img
              src={eventImages && eventImages["image_url_" + ratio]}
              alt=""
            />
          </picture>
        </div>
        <div className="hero__content">
          <img
            src={logoWithLines}
            alt="Parlor Social Club"
            className="hero__logo"
          />
          <h2 className="hero__heading">
            <span>{guestName && `${guestName}, `}You’re Invited</span>
            <div className="hero__subheading">
              by Member
              <span className="break-at-mobile">{invitedBy}</span>
            </div>
          </h2>
          <h1 className="hero__event-name">{eventName}</h1>
          <section className="details-container">
            <h2
              className="detail-text"
              style={isLiveEvent ? { visibility: "hidden" } : null}
            >
              {locationName}
            </h2>
            <p className="detail-text detail-text--date">
              {format(parseDate(eventDate), "iiii, MMM do k:mm")}
              <span className="text-lowercase">
                {format(parseDate(eventDate), "aa")}
              </span>
            </p>
            <p className="detail-text detail-text--lh">More details below</p>
          </section>
          {!isNominated && !guestName && (
            <section className="general-event-actions">
              <h2 className="general-event-actions__attend-text">To attend</h2>
              <Link
                className="outlined-button outlined-button--inverted"
                to="/application/sign-up"
              >
                Apply for Parlor Membership
              </Link>
              <p className="general-event-actions__description-text">
                or confirm with {invitedBy} <br /> to add you to the guest list
              </p>
            </section>
          )}
          {guestName && (
            <section className="with-invitation-actions">
              <p className="with-invitation-actions__attend-text">
                You are all set to attend the event as a guest!
                <br />
                If you want to see the full guest list and other attendees
              </p>
              <Link className={attendButtonClsx} to="/application/sign-up">
                Apply for Parlor Membership
              </Link>
              {guests && (
                <div className="with-invitation-actions__attending-guests">
                  <p>Other Attending Guests:</p>
                  <p>{guests.map((item) => item.name).join(", ")}</p>
                </div>
              )}
              <div className="with-invitation-actions__calendar-btn">
                <button
                  type="button"
                  className="underlined-button underlined-button--center"
                  onClick={() => {
                    const date = [
                      ...eventDate.split(" ")[0].split("-"),
                      ...eventDate.split(" ")[1].split(":"),
                    ]
                      .slice(0, 5)
                      .map((item) => Number(item));

                    const endDate = [
                      ...eventEnd.split(" ")[0].split("-"),
                      ...eventEnd.split(" ")[1].split(":"),
                    ]
                      .slice(0, 5)
                      .map((item) => Number(item));

                    addEvent({
                      title: eventName,
                      description: `${eventDescription} ${window.location.href}`,
                      location: locationName,
                      start: date,
                      end: endDate,
                    });
                  }}
                >
                  Add to my calendar
                </button>
              </div>
            </section>
          )}
        </div>
      </section>
      <section className="event-details">
        <div className="event-details__container">
          <h2 className="event-details__heading">Event details:</h2>
          <p className="event-description">
            <ReactMarkdown allowDangerousHtml>{eventDescription}</ReactMarkdown>
          </p>
          <div className="detail">
            <div className="detail__container">
              <img
                src={ticket}
                alt="ticket icon"
                className="detail__adornment"
              />
              <span className="detail__label">Entry:</span>
              <span className="detail__value">
                {eventTicketPrice === 0
                  ? "Free"
                  : ` ${eventTicketCurrency}${eventTicketPrice}`}
              </span>
            </div>
          </div>
          {!isLiveEvent && guestName && (
            <div
              className={clsx("detail", "detail--last", {
                "hide-on-medium": isLiveEvent,
                "mb-10": isLiveEvent,
              })}
            >
              <div className="detail__container detail__container--with-break">
                <img
                  src={pin2}
                  alt="map pin icon"
                  className="detail__adornment detail__adornment--pin"
                />
                <span className="detail__label">Location:</span>
                <span className="detail__value">
                  {locationName}
                  <br />
                  {eventLocation.address_1}
                </span>
              </div>
            </div>
          )}
          <div className="detail__cta-container hide-mobile">
            {!isLiveEvent && guestName && (
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${eventLocation?.lat},${eventLocation?.lng}`}
                className="outlined-button outlined-button--large outlined-button--map"
                target="_blank"
                rel="noreferrer noopener"
              >
                View on map
              </a>
            )}
            {isLiveEvent && shouldDisplayLiveBtn && guestName && (
              <Link
                to={`/share/${hash}/event/details`}
                className="outlined-button outlined-button--large outlined-button--map outlined-button--secondary"
              >
                Live
              </Link>
            )}
          </div>
        </div>
        <div className="map">
          <div className="map__container">
            {!isLiveEvent && (
              <GoogleMap
                location={{ lat: eventLocation.lat, lng: eventLocation.lng }}
              />
            )}
            {isLiveEvent && (
              <div className="live-event-map">
                <img src={pin} alt="pin" />
                <p className="live-event-map__text">This Event is Online</p>
              </div>
            )}
          </div>
        </div>
        <div className="detail__cta-container only-mobile">
          {!isLiveEvent && guestName && (
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${eventLocation.lat},${eventLocation.lng}`}
              className="outlined-button outlined-button--large outlined-button--full-width outlined-button--map"
              target="_blank"
              rel="noreferrer noopener"
            >
              View on map
            </a>
          )}
          {isLiveEvent && guestName && shouldDisplayLiveBtn && (
            <Link
              to={`/share/${hash}/event/details`}
              className="outlined-button outlined-button--large outlined-button--full-width outlined-button--map outlined-button--secondary"
            >
              Live
            </Link>
          )}
        </div>
      </section>
    </main>
  );
};

export default EventsView;
